import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  deletePoTemplate,
  listPoTemplates,
  savePoTemplate,
  trainPoTemplateSample,
} from "../../api/customerPoTemplateApi";

const { Dragger } = Upload;
const { Text, Title } = Typography;
const { Panel } = Collapse;

// Header fields are named after the Order model fields they populate.
const HEADER_FIELDS = [
  { key: "customer_po_number", label: "Order.customer_po_number", extractedKey: "po_number" },
  { key: "customer_po_date", label: "Order.customer_po_date", extractedKey: "po_date" },
  { key: "expected_delivery", label: "Order.expected_delivery", extractedKey: "expected_delivery" },
  { key: "shipping_address", label: "Order.shipping_address", extractedKey: "shipping_address" },
  { key: "billing_address", label: "Order.billing_address", extractedKey: "billing_address" },
  { key: "subtotal", label: "Order.subtotal", extractedKey: "subtotal" },
  { key: "tax_amount", label: "Order.tax_amount", extractedKey: "tax_amount" },
  { key: "total_amount", label: "Order.total_amount", extractedKey: "total_amount" },
  { key: "notes", label: "Order.notes", extractedKey: "notes" },
];

const ITEM_TARGETS = [
  { value: "ignore", label: "— Ignore this column —" },
  { value: "description", label: "Item Description" },
  { value: "quantity", label: "Quantity (cartons ordered)" },
  { value: "pieces_per_ctn", label: "Pieces per carton (master packing)" },
  { value: "total_pieces", label: "Total pieces" },
  { value: "dimensions", label: "Box dimensions" },
  { value: "rate", label: "Rate per unit" },
  { value: "amount", label: "Line amount" },
];

const ORDER_ITEM_TARGETS = [
  { value: "ignore", label: "-- Ignore / not saved --" },
  // Identity
  { value: "item_name", label: "OrderItem.item_name" },
  { value: "item_description", label: "OrderItem.item_description" },
  { value: "subcategory", label: "OrderItem.subcategory (box type / style)" },
  { value: "number_of_pieces", label: "OrderItem.number_of_pieces" },
  // Dimensions
  { value: "length_width_height", label: "OrderItem.length + width + height (combined)" },
  { value: "length", label: "OrderItem.length" },
  { value: "width", label: "OrderItem.width" },
  { value: "height", label: "OrderItem.height" },
  { value: "unit_type", label: "OrderItem.unit_type (inches / mm / cm)" },
  // Board spec
  { value: "ply", label: "OrderItem.ply" },
  { value: "casting_type", label: "OrderItem.casting_type" },
  { value: "joining_method", label: "OrderItem.joining_method (stitching / hand_pasting)" },
  // Box role & rating
  { value: "box_type", label: "OrderItem.box_type (inner / outer)" },
  { value: "rate_type", label: "OrderItem.rate_type" },
  { value: "packing_quantity_rs", label: "OrderItem.packing_quantity_rs (no. of inners)" },
  // Conversion & sheet packing
  { value: "top_conversion_rate", label: "OrderItem.top_conversion_rate" },
  { value: "box_conversion_rate", label: "OrderItem.box_conversion_rate" },
  { value: "rs", label: "OrderItem.rs (sheet size)" },
  { value: "cs", label: "OrderItem.cs (sheet size)" },
  { value: "boxes_per_sheet", label: "OrderItem.boxes_per_sheet" },
  { value: "sheets_per_box", label: "OrderItem.sheets_per_box" },
  // Order quantities
  { value: "quantity", label: "OrderItem.quantity" },
  { value: "rate_per_unit", label: "OrderItem.rate_per_unit" },
  { value: "total_amount", label: "OrderItem.total_amount" },
  { value: "item_remarks", label: "OrderItem.item_remarks" },
];

// Best-effort auto-detect of what a column labelled `header` (as printed on the PO) most likely
// maps to. Purely to save operator time — the operator can always override in the dropdown.
// Returns one of the `value`s from ORDER_ITEM_TARGETS.
function guessTargetForHeader(header) {
  const h = String(header || "").toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
  if (!h) return "ignore";
  const has = (...needles) => needles.some((n) => h.includes(n));
  // Order matters — check the most specific patterns first.
  if (has("hsn", "sac")) return "ignore";
  if (has("uom", "unit of", "uoi")) return "ignore";
  if (has("sr no", "sl no", "s no", "line", "s.no")) return "ignore";
  if (has("gst", "tax %", "tax%", "cgst", "sgst", "igst")) return "ignore";
  if (h === "size" || has("dimension", "size (", "l x w", "l*w", "lxw", "lxbxh", "lxwxh")) return "length_width_height";
  if (has("length")) return "length";
  if (has("width", "breadth")) return "width";
  if (has("height", "depth")) return "height";
  if (has("ply", "walls", "wall")) return "ply";
  if (has("qty", "quantity", "order qty")) return "quantity";
  if (has("rate", "unit price", "price")) return "rate_per_unit";
  if (has("amount", "total", "value")) return "total_amount";
  if (has("remark", "note", "comment", "spec")) return "item_remarks";
  if (has("description", "item name", "particular", "goods", "des", "product")) return "item_name";
  if (has("inner")) return "packing_quantity_rs";
  if (has("box type", "type of box", "inner/outer", "inner outer")) return "box_type";
  if (has("subcategory", "box style", "carton type", "style")) return "subcategory";
  if (has("casting", "die")) return "casting_type";
  if (has("glue", "join", "stitch", "past")) return "joining_method";
  return "ignore";
}

const EXTRACTED_ITEM_PREVIEW_COLUMNS = [
  {
    title: "#",
    dataIndex: "line_no",
    key: "line_no",
    width: 56,
    render: (value, _, index) => value || index + 1,
  },
  {
    title: "Description",
    dataIndex: "item_name",
    key: "item_name",
    render: (value, record) => value || record.description || "—",
  },
  {
    title: "Dimensions",
    key: "dimensions",
    render: (_, record) => {
      // Prefer original printed dimensions + unit (what the customer wrote on the PO,
      // e.g. "16.5 × 12.4 × 4.4 inches"). Fall back to the mm-normalised copy only if the
      // extractor couldn't preserve the source unit.
      const hasOrig = record.length != null || record.width != null || record.height != null;
      const dims = hasOrig
        ? [record.length, record.width, record.height]
        : [record.length_mm, record.width_mm, record.height_mm];
      const filtered = dims.filter((v) => v !== null && v !== undefined && v !== "");
      if (!filtered.length) return "—";
      const unit = hasOrig ? (record.unit_type || "inches") : "mm";
      return `${filtered.join(" × ")} ${unit}`;
    },
  },
  {
    title: "Ply",
    dataIndex: "ply",
    key: "ply",
    width: 72,
    render: (value) => value || "—",
  },
  {
    title: "Qty",
    dataIndex: "quantity",
    key: "quantity",
    width: 90,
    render: (value) => (value != null ? value : "—"),
  },
  {
    title: "UOM",
    dataIndex: "uom",
    key: "uom",
    width: 84,
    render: (value) => value || "—",
  },
  {
    title: "Rate",
    dataIndex: "rate_per_unit",
    key: "rate_per_unit",
    width: 100,
    render: (value, record) => {
      const v = value ?? record.rate;
      return v != null ? v : "—";
    },
  },
  {
    title: "Amount",
    dataIndex: "total_amount",
    key: "total_amount",
    width: 110,
    render: (value, record) => {
      const v = value ?? record.amount;
      return v != null ? v : "—";
    },
  },
];

// Escapes a literal value into a regex pattern. On its own this only ever matches THIS exact
// sample again — used only as a last-resort fallback when no stable label can be found.
function escapeRegex(str) {
  return String(str || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Fields whose value is short and reliably preceded by a fixed label on the same line
// (e.g. "P.O.#." before the number, "Dated :-" before the date, "TOTAL" before the amount) —
// auto-suggested by anchoring on that label rather than the value itself, so the suggested
// pattern actually generalizes to a DIFFERENT PO from the same customer, not just this sample.
const LABEL_ANCHORABLE_HEADER_KEYS = new Set([
  "customer_po_number",
  "customer_po_date",
  "expected_delivery",
  "subtotal",
  "tax_amount",
  "total_amount",
]);

// Finds the label text immediately preceding `value` on the same line in `rawText` and builds
// a pattern anchored on that label with a capture group, instead of matching the literal value
// (which would only ever match this one sample again). Falls back to a literal-value escape —
// clearly worse, but still functional for a re-upload of the identical file — when no stable
// label-like text precedes the value.
function buildLabelAnchoredPattern(rawText, value) {
  const text = String(value ?? "").trim();
  if (!text || !rawText) return "";
  const idx = rawText.indexOf(text);
  if (idx === -1) return escapeRegex(text);

  const lineStart = rawText.lastIndexOf("\n", idx - 1) + 1;
  const before = rawText.slice(lineStart, idx);
  const labelMatch = before.match(/([A-Za-z][A-Za-z0-9 .#:-]{1,40})\s*$/);
  if (!labelMatch || labelMatch[1].trim().length < 2) return escapeRegex(text);

  return `${escapeRegex(labelMatch[1].trim())}\\s*:?-?\\.?\\s*(\\S+)`;
}

// Best-effort read of the first markdown pipe-table's header row(s), just to pre-fill suggested
// column names for the user to confirm/relabel — not the source of truth (templateExtractor.js
// on the backend re-parses the table itself at extraction time, using the same logic below).
// Mirrors templateExtractor.js's parseMarkdownTable: some POs split a header across more than
// one row before the separator line, and some have blank/unlabeled columns (e.g. an unnamed
// size column) — those are kept as "" rather than dropped, so positions stay meaningful and the
// user can still label them by eye even with no printed heading to go on.
function guessTableHeaders(rawText) {
  const lines = (rawText || "").split("\n").map((l) => l.trim()).filter(Boolean);

  const pipeRows = [];
  let started = false;
  for (const line of lines) {
    const isPipeRow = line.startsWith("|") && line.endsWith("|");
    if (isPipeRow) {
      pipeRows.push(line);
      started = true;
    } else if (started) {
      break;
    }
  }
  if (pipeRows.length < 2) return [];

  const splitRow = (line) => line.slice(1, -1).split("|").map((c) => c.trim());
  const isSeparatorRow = (cells) =>
    cells.some((c) => /^:?-{2,}:?$/.test(c)) && cells.every((c) => c === "" || /^:?-{2,}:?$/.test(c));

  const allRows = pipeRows.map(splitRow);
  const numCols = Math.max(...allRows.map((r) => r.length));
  const pad = (r) => (r.length < numCols ? [...r, ...Array(numCols - r.length).fill("")] : r);

  let sepIdx = allRows.findIndex(isSeparatorRow);
  if (sepIdx === -1) sepIdx = 1;

  const headerRows = allRows.slice(0, sepIdx).map(pad);
  const headers = [];
  for (let c = 0; c < numCols; c++) {
    const parts = headerRows.map((r) => r[c]).filter(Boolean);
    headers.push(parts.join(" ").trim());
  }
  return headers;
}

function buildFallbackItemColumns(extracted) {
  const firstItem =
    Array.isArray(extracted?.items) && extracted.items.length > 0 ? extracted.items[0] : null;
  if (!firstItem) return [];

  const hasDimensions =
    firstItem.length_mm != null || firstItem.width_mm != null || firstItem.height_mm != null;

  const columns = [];

  if (firstItem.line_no != null) {
    columns.push({ header: "Line No.", target: "ignore" });
  }
  if (firstItem.description) {
    columns.push({ header: "Description", target: "item_name" });
  }
  if (hasDimensions) {
    columns.push({ header: "Size / Dimensions", target: "length_width_height" });
  }
  if (firstItem.quantity != null) {
    columns.push({ header: "Quantity", target: "quantity" });
  }
  if (firstItem.uom) {
    columns.push({ header: "UOM", target: "ignore" });
  }
  if (firstItem.rate != null) {
    columns.push({ header: "Rate", target: "rate_per_unit" });
  }
  if (firstItem.amount != null) {
    columns.push({ header: "Amount", target: "total_amount" });
  }
  if (firstItem.hsn) {
    columns.push({ header: "HSN", target: "ignore" });
  }
  if (firstItem.tax_percent != null) {
    columns.push({ header: "Tax %", target: "ignore" });
  }
  if (firstItem.ply != null) {
    columns.push({ header: "Ply", target: "ply" });
  }

  return columns;
}

function normalizeOrderItemTarget(target) {
  const legacyTargets = {
    description: "item_name",
    quantity: "quantity",
    pieces_per_ctn: "ignore",
    total_pieces: "ignore",
    dimensions: "length_width_height",
    rate: "rate_per_unit",
    amount: "total_amount",
  };
  const isLegacyTarget = ITEM_TARGETS.some((option) => option.value === target);
  if (isLegacyTarget) return legacyTargets[target] || "ignore";
  return target || "ignore";
}

const PoTemplateTrainer = ({ customerId }) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]); // a customer can have several (one per PO layout)
  const [addingSample, setAddingSample] = useState(false);
  const [training, setTraining] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sample, setSample] = useState(null); // { previewUrl, ingestionId, rawText, extracted }
  const [headerPatterns, setHeaderPatterns] = useState({});
  const [customerParty, setCustomerParty] = useState(null);
  const [itemColumns, setItemColumns] = useState([]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const list = await listPoTemplates(customerId);
      setTemplates(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load PO templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleBeforeUpload = async (file) => {
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are supported");
      return false;
    }
    setTraining(true);
    try {
      const result = await trainPoTemplateSample(customerId, file);
      const extracted = result.extracted || {};

      setSample({
        previewUrl: URL.createObjectURL(file),
        ingestionId: result.ingestion_id,
        rawText: result.raw_text || "",
        extracted,
      });

      // Pre-fill header patterns. For short, reliably-labelled fields (PO number/date/amounts)
      // anchor on the preceding label so the pattern actually generalizes to a different PO.
      // Free-text fields (addresses, notes) rarely have a stable anchor and the value itself
      // will differ on every PO — leave those blank rather than silently saving a pattern that
      // only ever matches this one sample again; the operator can author one by hand if this
      // customer's layout happens to have a stable label for it.
      const patterns = {};
      HEADER_FIELDS.forEach(({ key, extractedKey }) => {
        const extractedValue = extracted[extractedKey || key];
        if (!extractedValue) return;
        if (LABEL_ANCHORABLE_HEADER_KEYS.has(key)) {
          patterns[key] = buildLabelAnchoredPattern(result.raw_text, extractedValue);
        }
      });
      setHeaderPatterns(patterns);

      // Default to the first party as "the customer" (typically the letterhead) — user confirms/corrects.
      setCustomerParty(Array.isArray(extracted.parties) && extracted.parties.length > 0 ? 0 : null);

      // Pre-fill item columns from the raw table header row when possible; if the OCR text
      // didn't preserve printed headers cleanly, fall back to the structured extracted item shape
      // so the user still gets a usable mapping grid instead of an almost blank state.
      const headers = guessTableHeaders(result.raw_text);
      if (headers.length > 0) {
        // Auto-suggest a target per column so the operator starts with a mostly-correct
        // mapping instead of every column stuck on "ignore".
        setItemColumns(headers.map((header) => ({ header, target: guessTargetForHeader(header) })));
      } else {
        setItemColumns(buildFallbackItemColumns(extracted));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Training extraction failed");
    } finally {
      setTraining(false);
    }
    return false; // prevent antd's default upload
  };

  const handleSave = async () => {
    const columns = {};
    // `index` is the column's left-to-right position as labelled — used as a fallback whenever
    // header text is blank or drifts slightly between POs (e.g. an unlabeled size column).
    itemColumns.forEach(({ header, target }, idx) => {
      const schemaTarget = normalizeOrderItemTarget(target);
      if (schemaTarget !== "ignore") columns[schemaTarget] = { header: header || "", index: idx };
    });

    const header = {};
    Object.entries(headerPatterns).forEach(([key, pattern]) => {
      if (pattern) header[key] = { pattern };
    });

    const fieldMappings = {
      customer_party: customerParty != null ? `party_${customerParty}` : null,
      header,
      items_table: {
        header_hints: itemColumns.map((c) => c.header),
        columns,
      },
    };

    setSaving(true);
    try {
      const saved = await savePoTemplate(customerId, {
        source_ingestion_id: sample.ingestionId,
        field_mappings: fieldMappings,
      });
      setTemplates((prev) => [saved, ...prev]);
      setSample(null);
      setAddingSample(false);
      toast.success("PO template saved — future orders matching this layout will extract automatically.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    try {
      await deletePoTemplate(templateId);
      setTemplates((prev) => prev.filter((t) => t.id !== templateId));
      toast.success("PO template removed");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove template");
    }
  };

  const itemColumnRows = useMemo(
    () =>
      itemColumns.map((col, idx) => ({
        key: idx,
        header: col.header,
        target: normalizeOrderItemTarget(col.target),
      })),
    [itemColumns]
  );

  const extractedHeaderValues = useMemo(() => {
    const extracted = sample?.extracted || {};
    return HEADER_FIELDS.reduce((acc, field) => {
      acc[field.key] = extracted[field.extractedKey || field.key] || "";
      return acc;
    }, {});
  }, [sample]);

  const extractedItems = useMemo(
    () =>
      Array.isArray(sample?.extracted?.items)
        ? sample.extracted.items.map((item, index) => ({ key: index, ...item }))
        : [],
    [sample]
  );

  const setItemTarget = (idx, target) => {
    setItemColumns((cols) => cols.map((c, i) => (i === idx ? { ...c, target } : c)));
  };

  const setItemHeader = (idx, header) => {
    setItemColumns((cols) => cols.map((c, i) => (i === idx ? { ...c, header } : c)));
  };

  const addItemColumn = () => {
    setItemColumns((cols) => [...cols, { header: "", target: "ignore" }]);
  };

  const removeItemColumn = (idx) => {
    setItemColumns((cols) => cols.filter((_, i) => i !== idx));
  };

  return (
    <Card title="PO Extraction Template" style={{ marginTop: 16 }}>
      {loading ? (
        <Text type="secondary">Loading…</Text>
      ) : sample && sample.extracted ? (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Confirm how this customer's PO maps to our order fields"
            description="This runs once. Every future PO from this customer will be read the same way, deterministically — no AI guessing involved."
          />
          <Row gutter={16}>
            <Col span={10}>
              <iframe
                title="Sample PO preview"
                src={sample.previewUrl}
                style={{ width: "100%", height: 480, border: "1px solid #eee" }}
              />
            </Col>
            <Col span={14}>
              <Title level={5}>Who is the customer on this document?</Title>
              {Array.isArray(sample.extracted.parties) && sample.extracted.parties.length > 0 ? (
                <Radio.Group
                  value={customerParty}
                  onChange={(e) => setCustomerParty(e.target.value)}
                  style={{ marginBottom: 16 }}
                >
                  <Space direction="vertical">
                    {sample.extracted.parties.map((p, idx) => (
                      <Radio key={idx} value={idx}>
                        {p.name} {p.role_hint ? <Tag>{p.role_hint}</Tag> : null}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              ) : (
                <Text type="secondary">No separate parties detected on this sample.</Text>
              )}

              {sample.extracted.buyer && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={`Detected buyer: ${sample.extracted.buyer.name || "—"}`}
                  description={
                    [
                      sample.extracted.buyer.address,
                      sample.extracted.buyer.gstin ? `GSTIN: ${sample.extracted.buyer.gstin}` : null,
                      sample.extracted.buyer.phone ? `Phone: ${sample.extracted.buyer.phone}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "No extra buyer details detected."
                  }
                />
              )}

              <Title level={5}>Order details fields</Title>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Check these patterns before saving — a wrong one silently breaks on the next PO"
                description="Fields marked (auto-suggested) were built by anchoring on the label text next to the value on this sample (e.g. finding 'P.O.#.' before the number) so they should generalize — but verify against the raw OCR text below. Fields left blank had no reliable label to anchor on; they'll simply come back empty on future POs unless you write a pattern by hand."
              />
              {HEADER_FIELDS.map(({ key, label }) => {
                const isAnchorable = LABEL_ANCHORABLE_HEADER_KEYS.has(key);
                return (
                  <div key={key} style={{ marginBottom: 8 }}>
                    <Text style={{ display: "inline-block", width: 150 }}>
                      {label}
                      {isAnchorable && headerPatterns[key] ? (
                        <Tag color="blue" style={{ marginLeft: 4, fontSize: 10 }}>
                          auto-suggested
                        </Tag>
                      ) : null}
                    </Text>
                    <Input
                      style={{ width: "calc(100% - 160px)" }}
                      placeholder={
                        isAnchorable
                          ? "regex pattern to locate this field"
                          : "no reliable anchor found — write a pattern by hand, or leave blank to skip"
                      }
                      value={headerPatterns[key] || ""}
                      onChange={(e) =>
                        setHeaderPatterns((p) => ({ ...p, [key]: e.target.value }))
                      }
                    />
                    {extractedHeaderValues[key] ? (
                      <div style={{ marginLeft: 150, marginTop: 4 }}>
                        <Text type="secondary">Detected value: {String(extractedHeaderValues[key])}</Text>
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {(sample.extracted.currency ||
                sample.extracted.subtotal != null ||
                sample.extracted.tax_amount != null ||
                sample.extracted.total_amount != null) && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 12, marginBottom: 16 }}
                  message="Detected order totals"
                  description={[
                    sample.extracted.currency ? `Currency: ${sample.extracted.currency}` : null,
                    sample.extracted.subtotal != null ? `Subtotal: ${sample.extracted.subtotal}` : null,
                    sample.extracted.tax_amount != null ? `Tax: ${sample.extracted.tax_amount}` : null,
                    sample.extracted.total_amount != null ? `Total: ${sample.extracted.total_amount}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              )}

              <Title level={5} style={{ marginTop: 16 }}>
                Item table columns
              </Title>
              {itemColumnRows.length === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="Couldn't auto-detect the item table's columns from this sample"
                  description={`Check the raw OCR text below, then add each column by hand with "+ Add column" — left to right, in the order they are printed on the PO (leave the header text blank for a column that has no printed heading, e.g. an unlabeled size column).`}
                />
              )}
              <Table
                size="small"
                pagination={false}
                dataSource={itemColumnRows}
                columns={[
                  {
                    title: "Column header (as printed)",
                    dataIndex: "header",
                    render: (header, row) => (
                      <Input
                        value={header}
                        placeholder="(no header text on the PDF)"
                        onChange={(e) => setItemHeader(row.key, e.target.value)}
                      />
                    ),
                  },
                  {
                    title: "Maps to OrderItem schema field",
                    dataIndex: "target",
                    render: (target, row) => (
                      <Select
                        value={target}
                        style={{ width: 220 }}
                        options={ORDER_ITEM_TARGETS}
                        onChange={(v) => setItemTarget(row.key, v)}
                      />
                    ),
                  },
                  {
                    title: "",
                    dataIndex: "remove",
                    render: (_, row) => (
                      <Button type="text" danger size="small" onClick={() => removeItemColumn(row.key)}>
                        Remove
                      </Button>
                    ),
                  },
                ]}
              />
              <Button type="dashed" onClick={addItemColumn} style={{ marginTop: 8, width: "100%" }}>
                + Add column
              </Button>

              <Title level={5} style={{ marginTop: 16 }}>
                Extracted items preview
              </Title>
              {extractedItems.length > 0 ? (
                <Table
                  size="small"
                  pagination={false}
                  scroll={{ x: 900 }}
                  dataSource={extractedItems}
                  columns={EXTRACTED_ITEM_PREVIEW_COLUMNS}
                />
              ) : (
                <Text type="secondary">No structured line items were detected in this sample.</Text>
              )}

              <Collapse ghost style={{ marginTop: 16 }}>
                <Panel header="Raw OCR text (for reference)" key="raw">
                  <pre
                    style={{
                      maxHeight: 240,
                      overflow: "auto",
                      fontSize: 12,
                      whiteSpace: "pre-wrap",
                      background: "#fafafa",
                      padding: 8,
                    }}
                  >
                    {sample.rawText || "(no OCR text captured for this sample)"}
                  </pre>
                </Panel>
              </Collapse>

              <Space style={{ marginTop: 16 }}>
                <Button type="primary" loading={saving} onClick={handleSave}>
                  Save Template
                </Button>
                <Button onClick={() => setSample(null)}>Cancel</Button>
              </Space>
            </Col>
          </Row>
        </div>
      ) : addingSample ? (
        <div>
          <Text type="secondary">
            Upload a sample PO that represents a layout this customer uses. If they send POs in
            more than one layout (different department, different software, etc.), train one
            sample per layout — each is saved as its own template.
          </Text>
          <Dragger
            accept=".pdf"
            multiple={false}
            showUploadList={false}
            beforeUpload={handleBeforeUpload}
            disabled={training}
            style={{ marginTop: 16 }}
          >
            <p>
              <InboxOutlined style={{ fontSize: 32, color: "#4f46e5" }} />
            </p>
            <p>{training ? "Extracting sample PO…" : "Drop a sample PO PDF here"}</p>
          </Dragger>
          <Button style={{ marginTop: 12 }} onClick={() => setAddingSample(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div>
          {templates.length === 0 ? (
            <Text type="secondary">
              Not trained yet — every PO from this customer currently goes through the generic AI
              extraction. Upload one sample PO to teach the system this customer's exact layout;
              every PO after that will extract deterministically, with no AI call at all.
            </Text>
          ) : (
            <Space direction="vertical" style={{ width: "100%" }}>
              {templates.map((t) => (
                <Card key={t.id} size="small" type="inner" title={t.label || "Untitled layout"}>
                  <Tag color={t.status === "ready" ? "green" : "orange"}>
                    {t.status === "ready" ? "Ready" : "Draft — needs review"}
                  </Tag>
                  <Text> Used {t.times_used} time(s)</Text>
                  {t.last_used_at && (
                    <Text type="secondary"> · last used {new Date(t.last_used_at).toLocaleString()}</Text>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <Button danger size="small" onClick={() => handleDelete(t.id)}>
                      Remove template
                    </Button>
                  </div>
                </Card>
              ))}
            </Space>
          )}
          <Button type="dashed" style={{ marginTop: 16, width: "100%" }} onClick={() => setAddingSample(true)}>
            + Train from a new sample
          </Button>
        </div>
      )}
    </Card>
  );
};

export default PoTemplateTrainer;
